/** Tab-übergreifende Navigation ohne Router: allgemeines Navigationsziel
 *  (Ansicht + optional Tab + Parameter), z. B. aus Dashboard-Kacheln oder der
 *  Belegkarte. App wechselt die Ansicht (und reicht Tab/Parameter an die
 *  ManagementPage durch, wenn diese noch nicht gemountet ist); eine bereits
 *  gemountete ManagementPage wendet Tab und Parameter selbst an.
 *  Verstandene Parameter: `auftrag_id` (öffnet die Auftragsakte), `partner_id`
 *  (öffnet die Kundenakte im Kontakte-Tab), `vertrag_id` (öffnet die
 *  Vertragsakte im Verträge-Tab), `datum` (yyyy-mm-dd, mit Tab
 *  `einsaetze`: öffnet die Plantafel in der Woche dieses Tages). */
export const NAVIGIERE_EVENT = 'erp:navigiere';

export interface NavigiereDetail {
  /** `einstellungen`: öffnet den Einstellungs-Drawer, `tab` = Sektions-Id (z. B. `benutzer`). */
  ansicht: 'management' | 'chat' | 'einstellungen';
  tab: string | null;
  params: Record<string, unknown>;
}

export function navigiere(ziel: NavigiereDetail): void {
  window.dispatchEvent(new CustomEvent<NavigiereDetail>(NAVIGIERE_EVENT, { detail: ziel }));
}

export function navigiereZuAuftrag(auftragId: number): void {
  navigiere({ ansicht: 'management', tab: 'auftraege', params: { auftrag_id: auftragId } });
}

export function navigiereZuPartner(partnerId: number): void {
  navigiere({ ansicht: 'management', tab: 'kunden', params: { partner_id: partnerId } });
}

export function navigiereZuVertrag(vertragId: number): void {
  navigiere({ ansicht: 'management', tab: 'vertraege', params: { vertrag_id: vertragId } });
}

// Modul-weiter Flag statt Kontext/Prop-Drilling: die Chips, die
// navigiereZuAuftrag auslösen, sitzen in RegieberichtForm/-Overview, weit weg
// von ManagementPage. ManagementPage (sichtbareTabs) und DashboardsPage
// (lib/tabRollen.sichtbareTabIds, für die Belegkarten der Kacheln) wissen, ob
// die aktuelle Rolle den Aufträge-Tab überhaupt sieht - und pflegen dieses
// Flag nach (setAuftragNavigationVerfuegbar).
// Default true (optimistisch): ManagementPage ist immer die Wurzel, unter der
// diese Chips gerendert werden, ihr Effekt läuft also vor jedem relevanten
// Render - ein Default von false würde nur einen unnötigen Flackerer für die
// Mehrheit erzeugen, die navigieren darf.
let auftragNavigationVerfuegbar = true;

export function setAuftragNavigationVerfuegbar(verfuegbar: boolean): void {
  auftragNavigationVerfuegbar = verfuegbar;
}

/** Darf navigiereZuAuftrag() bei der aktuellen Rolle überhaupt etwas
 *  bewirken? Rollen ohne Aufträge-Tab (Projektleiter/Projektmitarbeiter/
 *  Buchhaltung) landen sonst auf dem Standard-Tab der Verwaltung - die Chips
 *  blenden sich hier konsequent aus, statt tote Links anzuzeigen. */
export function kannZuAuftragNavigieren(): boolean {
  return auftragNavigationVerfuegbar;
}

// Wie beim Auftrag: ManagementPage pflegt das Flag nach, ob der Kontakte-Tab
// (`kunden`) für die aktuelle Rolle sichtbar ist. Default true (optimistisch).
let partnerNavigationVerfuegbar = true;

export function setPartnerNavigationVerfuegbar(verfuegbar: boolean): void {
  partnerNavigationVerfuegbar = verfuegbar;
}

/** Darf navigiereZuPartner() bei der aktuellen Rolle überhaupt etwas bewirken?
 *  Rollen ohne Kontakte-Tab sehen den Link nicht, statt eines toten Links. */
export function kannZuPartnerNavigieren(): boolean {
  return partnerNavigationVerfuegbar;
}

// Wie beim Auftrag: ManagementPage pflegt das Flag nach, ob der Verträge-Tab
// für die aktuelle Rolle sichtbar ist. Default true (optimistisch).
let vertragNavigationVerfuegbar = true;

export function setVertragNavigationVerfuegbar(verfuegbar: boolean): void {
  vertragNavigationVerfuegbar = verfuegbar;
}

/** Darf navigiereZuVertrag() bei der aktuellen Rolle überhaupt etwas bewirken? */
export function kannZuVertragNavigieren(): boolean {
  return vertragNavigationVerfuegbar;
}
